/**
 * The family's decisions, taken once, away from any composition.
 *
 * Four surfaces ask the same four questions — which serving is the headline
 * figure stated against, what does that serving actually say, which unit is a
 * micronutrient in, and how does a recipe's two clocks read — and four answers
 * is four places for one of them to drift.
 *
 * The micronutrient helper is the load-bearing one. The two endpoints publish
 * the same four keys under two incompatible unit systems, so every figure
 * leaves this module carrying its own unit rather than being handed to a
 * renderer that has to remember which endpoint it came from.
 */
import {
  CNUTRITIONCARD_CARBOHYDRATE_LABEL,
  CNUTRITIONCARD_COOK_LABEL,
  CNUTRITIONCARD_ENERGY_UNIT,
  CNUTRITIONCARD_FAT_LABEL,
  CNUTRITIONCARD_MINUTES_UNIT,
  CNUTRITIONCARD_PREP_LABEL,
  CNUTRITIONCARD_PROTEIN_LABEL,
  CNUTRITIONCARD_SERVING_PREFIX,
  CNUTRITION_UNIT_GRAMS,
  C_NUTRITION_MICRO_LABEL,
  C_NUTRITION_MICRO_UNIT,
} from "./cnutritioncard.constants";
import {
  CnutritioncardKind,
  CnutritioncardMicronutrient,
  type CnutritioncardData,
  type CnutritioncardMicronutrients,
  type CnutritioncardNutrients,
  type CnutritioncardServing,
} from "./cnutritioncard.types";

const FACT_SEPARATOR = " · ";
const VALUE_SEPARATOR = " ";
const METRIC_SEPARATOR = " ";

/** The row key: the vendor's own id, which both endpoints carry. */
export function cnutritioncardKey(data: CnutritioncardData): string {
  return data.key;
}

/**
 * The serving every headline figure on this card is stated against: the first
 * one the endpoint sent.
 *
 * The vendor's order is kept rather than re-ranked. A food's first serving is
 * its metric base (`100 g` on the captured record) and a recipe's only serving
 * is its own `1 serving`, so first is already the right answer on both
 * endpoints — and picking "the 100g one" would silently change which number a
 * reader compares the moment a record arrives without it.
 */
export function cnutritioncardLeadServing(
  data: CnutritioncardData,
): CnutritioncardServing | null {
  return data.servings[0] ?? null;
}

/**
 * What a figure is measured against, in the vendor's own words: `per 100 g`.
 *
 * The household description leads because it is what a reader recognises; the
 * metric pair is used only where there is no description, which is the shape
 * the search rows arrive in.
 */
export function cnutritioncardServingBasis(
  serving: CnutritioncardServing | null,
): string | null {
  if (serving == null) {
    return null;
  }
  if (serving.description != null && serving.description.length > 0) {
    return `${CNUTRITIONCARD_SERVING_PREFIX} ${serving.description}`;
  }
  if (serving.metricAmount == null || serving.metricUnit == null) {
    return null;
  }
  return `${CNUTRITIONCARD_SERVING_PREFIX} ${String(serving.metricAmount)}${METRIC_SEPARATOR}${serving.metricUnit}`;
}

/** The energy figure as a string, or `null` where the record published none. */
export function cnutritioncardEnergy(
  serving: CnutritioncardServing | null,
): string | null {
  const calories = serving?.nutrients.calories ?? null;
  return calories == null ? null : String(calories);
}

export interface CnutritioncardMacro {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly unit: string;
}

/**
 * The three macros beside the energy figure, in the vendor's own order, and
 * only the ones this record filled.
 *
 * Carbohydrate is `0` on every captured chicken serving, which is a MEASURED
 * zero and stays on the card; a `null` is an absent measurement and is dropped.
 * Collapsing the two would make a food that was never analysed look like a food
 * with no carbohydrate in it.
 */
export function cnutritioncardMacros(
  nutrients: CnutritioncardNutrients,
): readonly CnutritioncardMacro[] {
  return [
    {
      key: CNUTRITIONCARD_CARBOHYDRATE_LABEL,
      label: CNUTRITIONCARD_CARBOHYDRATE_LABEL,
      value: nutrients.carbohydrate,
      unit: CNUTRITION_UNIT_GRAMS,
    },
    {
      key: CNUTRITIONCARD_FAT_LABEL,
      label: CNUTRITIONCARD_FAT_LABEL,
      value: nutrients.fat,
      unit: CNUTRITION_UNIT_GRAMS,
    },
    {
      key: CNUTRITIONCARD_PROTEIN_LABEL,
      label: CNUTRITIONCARD_PROTEIN_LABEL,
      value: nutrients.protein,
      unit: CNUTRITION_UNIT_GRAMS,
    },
  ].filter(
    (macro): macro is CnutritioncardMacro =>
      macro.value != null && Number.isFinite(macro.value),
  );
}

export interface CnutritioncardMicroEntry {
  readonly key: CnutritioncardMicronutrient;
  readonly label: string;
  readonly value: number;
  /** The unit THIS endpoint measured it in. Never assumed by the renderer. */
  readonly unit: string;
}

/**
 * The micronutrient panel, with every figure already carrying its own unit.
 *
 * This is the one place the two endpoints' disagreement is resolved, and it is
 * resolved by keeping it: a metric panel prints `28 mcg` where a daily-percent
 * panel prints `0 % of daily intake`, and the two never share a unit lookup.
 */
export function cnutritioncardMicros(
  micronutrients: CnutritioncardMicronutrients | null,
): readonly CnutritioncardMicroEntry[] {
  if (micronutrients == null) {
    return [];
  }
  const units = C_NUTRITION_MICRO_UNIT[micronutrients.basis];
  return [
    {
      key: CnutritioncardMicronutrient.VitaminA,
      value: micronutrients.vitaminA,
    },
    {
      key: CnutritioncardMicronutrient.VitaminC,
      value: micronutrients.vitaminC,
    },
    { key: CnutritioncardMicronutrient.Calcium, value: micronutrients.calcium },
    { key: CnutritioncardMicronutrient.Iron, value: micronutrients.iron },
  ]
    .filter(
      (entry): entry is { key: CnutritioncardMicronutrient; value: number } =>
        entry.value != null && Number.isFinite(entry.value),
    )
    .map((entry) => ({
      key: entry.key,
      label: C_NUTRITION_MICRO_LABEL[entry.key],
      value: entry.value,
      unit: units[entry.key],
    }));
}

/**
 * A recipe's two clocks as one line: `10 min prep · 12 min cook`.
 *
 * `null` where the record carries neither, which is every food row — a food is
 * not cooked by this endpoint and a `0 min` there would be a claim.
 */
export function cnutritioncardTimeLine(
  data: CnutritioncardData,
): string | null {
  const parts = [
    data.preparationTimeMinutes == null
      ? null
      : `${String(data.preparationTimeMinutes)} ${CNUTRITIONCARD_MINUTES_UNIT} ${CNUTRITIONCARD_PREP_LABEL}`,
    data.cookingTimeMinutes == null
      ? null
      : `${String(data.cookingTimeMinutes)} ${CNUTRITIONCARD_MINUTES_UNIT} ${CNUTRITIONCARD_COOK_LABEL}`,
  ].filter((part): part is string => part != null);
  return parts.length === 0 ? null : parts.join(FACT_SEPARATOR);
}

/**
 * The identity line under the name: the brand where the row has one, and
 * otherwise the vendor's own kind word.
 *
 * One of the three captured food rows carries a brand. The other two are
 * `Generic`, which is a real distinction a reader acts on — a generic chicken
 * breast and Kirkland's are not the same row — so the kind is printed where the
 * brand is absent rather than the line going blank.
 */
export function cnutritioncardOrigin(data: CnutritioncardData): string | null {
  if (data.brand != null && data.brand.length > 0) {
    return data.brand;
  }
  return data.vendorKind != null && data.vendorKind.length > 0
    ? data.vendorKind
    : null;
}

/** The energy figure with its unit, for the surfaces that print it inline. */
export function cnutritioncardEnergyLine(
  serving: CnutritioncardServing | null,
): string | null {
  const energy = cnutritioncardEnergy(serving);
  return energy == null
    ? null
    : `${energy}${VALUE_SEPARATOR}${CNUTRITIONCARD_ENERGY_UNIT}`;
}

/** How many alternative servings the panel holds beyond the one on the face. */
export function cnutritioncardAlternateServingCount(
  data: CnutritioncardData,
): number {
  return Math.max(0, data.servings.length - 1);
}

/** True for the endpoint that publishes pictures. Never inferred from `images`. */
export function cnutritioncardCarriesImages(data: CnutritioncardData): boolean {
  return data.kind === CnutritioncardKind.Recipe;
}
